import React, { useEffect, useState } from 'react';
import { db } from '../../firebase';
import { collection, onSnapshot, addDoc, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { Pencil, Trash2, PlusCircle, CheckCircle2, XCircle } from 'lucide-react';

const defaultForm = {
  question: '',
  options: ['', '', '', ''],
  answer: 0,
  explanation: '',
  active: true,
};

const Quiz = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editId, setEditId] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [adding, setAdding] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    const q = collection(db, 'quiz_efafc');
    const unsub = onSnapshot(q, (snap) => {
      setQuestions(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleOptionChange = (idx, value) => {
    const newOptions = [...form.options];
    newOptions[idx] = value;
    setForm({ ...form, options: newOptions });
  };

  const handleAddOption = () => {
    setForm({ ...form, options: [...form.options, ''] });
  };

  const handleRemoveOption = (idx) => {
    if (form.options.length <= 2) return;
    const newOptions = form.options.filter((_, i) => i !== idx);
    let newAnswer = form.answer;
    if (idx === form.answer) newAnswer = 0;
    else if (idx < form.answer) newAnswer = form.answer - 1;
    setForm({ ...form, options: newOptions, answer: newAnswer });
  };

  const handleAddOrEdit = async (e) => {
    e.preventDefault();
    setAdding(true);
    setFeedback('');
    try {
      const data = {
        question: form.question,
        options: form.options,
        answer: Number(form.answer),
        explanation: form.explanation,
        active: !!form.active,
        updatedAt: serverTimestamp(),
      };
      if (editId) {
        await updateDoc(doc(db, 'quiz_efafc', editId), data);
        setFeedback('Question modifiée !');
      } else {
        await addDoc(collection(db, 'quiz_efafc'), {
          ...data,
          createdAt: serverTimestamp(),
        });
        setFeedback('Question ajoutée !');
      }
      setShowModal(false);
      setForm(defaultForm);
      setEditId(null);
    } catch (err) {
      setFeedback("Erreur : " + err.message);
    }
    setAdding(false);
    setTimeout(() => setFeedback(''), 3000);
  };

  const handleEdit = (q) => {
    setForm({
      question: q.question || '',
      options: q.options || ['', '', '', ''],
      answer: typeof q.answer === 'number' ? q.answer : 0,
      explanation: q.explanation || '',
      active: q.active !== false,
    });
    setEditId(q.id);
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteDoc(doc(db, 'quiz_efafc', toDelete.id));
      setFeedback('Question supprimée.');
    } catch (err) {
      setFeedback("Erreur lors de la suppression : " + err.message);
    }
    setShowConfirm(false);
    setToDelete(null);
    setTimeout(() => setFeedback(''), 3000);
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Gestion des Quiz</h1>
      <button className="flex items-center gap-2 bg-[#FFE400] hover:bg-[#BDFF00] text-[#001B56] font-bold px-4 py-2 rounded-xl mb-6 shadow transition" onClick={() => { setShowModal(true); setEditId(null); setForm(defaultForm); }}>
        <PlusCircle size={20} /> Ajouter une question
      </button>
      {feedback && <div className="mb-4 text-white font-semibold">{feedback}</div>}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <form onSubmit={handleAddOrEdit} className="bg-[#00238D] text-white rounded-2xl shadow-2xl p-8 w-full max-w-2xl flex flex-col gap-4 animate-fade-in">
            <h2 className="text-xl font-bold mb-2">{editId ? 'Modifier la question' : 'Ajouter une question'}</h2>
            <div>
              <label className="block mb-1">Question</label>
              <textarea name="question" value={form.question} onChange={handleChange} required className="w-full px-3 py-2 rounded bg-[#001B56] text-white min-h-[60px]" />
            </div>
            <div>
              <label className="block mb-1">Options</label>
              <div className="flex flex-col gap-2">
                {form.options.map((opt, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={opt}
                      onChange={e => handleOptionChange(idx, e.target.value)}
                      required
                      className="flex-1 px-3 py-2 rounded bg-[#001B56] text-white"
                      placeholder={`Option ${idx + 1}`}
                    />
                    <input
                      type="radio"
                      name="answer"
                      checked={form.answer === idx}
                      onChange={() => setForm({ ...form, answer: idx })}
                      className="accent-[#FFE400]"
                      title="Bonne réponse"
                    />
                    <span className="text-xs">Bonne réponse</span>
                    {form.options.length > 2 && (
                      <button type="button" className="text-red-400 hover:text-red-600" onClick={() => handleRemoveOption(idx)} title="Supprimer cette option">✕</button>
                    )}
                  </div>
                ))}
                <button type="button" className="mt-2 px-3 py-1 bg-[#FFE400] text-[#001B56] rounded font-bold hover:bg-[#BDFF00] w-fit" onClick={handleAddOption}>+ Ajouter une option</button>
              </div>
            </div>
            <div>
              <label className="block mb-1">Explication (optionnel)</label>
              <textarea name="explanation" value={form.explanation} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white min-h-[40px]" />
            </div>
            <div className="flex items-center gap-3 mt-2">
              <input type="checkbox" name="active" checked={form.active} onChange={handleChange} id="active" />
              <label htmlFor="active">Question active (affichée dans le quiz)</label>
            </div>
            <div className="flex justify-end gap-4 mt-4">
              <button type="button" className="px-4 py-2 rounded bg-gray-400 hover:bg-gray-500 text-white font-bold" onClick={() => { setShowModal(false); setEditId(null); }} disabled={adding}>Annuler</button>
              <button type="submit" className="px-6 py-2 rounded bg-[#FFE400] hover:bg-[#BDFF00] text-[#001B56] font-bold" disabled={adding}>{adding ? (editId ? 'Modification...' : 'Ajout...') : (editId ? 'Modifier' : 'Ajouter')}</button>
            </div>
          </form>
        </div>
      )}
      {/* Confirmation suppression */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-[#00238D] text-white rounded-2xl shadow-2xl p-8 w-full max-w-md flex flex-col gap-6 animate-fade-in">
            <h2 className="text-xl font-bold">Confirmer la suppression</h2>
            <p>Voulez-vous vraiment supprimer cette question ? Cette action est irréversible.</p>
            <div className="flex justify-end gap-4">
              <button className="px-4 py-2 rounded bg-gray-400 hover:bg-gray-500 text-white font-bold" onClick={() => { setShowConfirm(false); setToDelete(null); }}>Annuler</button>
              <button className="px-6 py-2 rounded bg-red-600 hover:bg-red-700 text-white font-bold" onClick={handleDelete}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
      {loading ? (
        <div className="text-white">Chargement...</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-[#00238D] rounded-xl shadow-lg">
            <thead>
              <tr className="text-[#FFE400] text-left">
                <th className="py-2 px-4">Question</th>
                <th className="py-2 px-4">Options</th>
                <th className="py-2 px-4">Bonne réponse</th>
                <th className="py-2 px-4">Explication</th>
                <th className="py-2 px-4">Statut</th>
                <th className="py-2 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {questions.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-white py-8">Aucune question enregistrée.</td></tr>
              ) : (
                questions.map(q => (
                  <tr key={q.id} className="border-b border-[#001B56] hover:bg-[#001B56]/40 transition">
                    <td className="py-2 px-4 font-bold max-w-xs whitespace-pre-line">{q.question}</td>
                    <td className="py-2 px-4">
                      <ul className="list-disc ml-4 text-sm">
                        {q.options?.map((opt, idx) => (
                          <li key={idx} className={q.answer === idx ? 'text-[#BDFF00] font-bold' : ''}>{opt}</li>
                        ))}
                      </ul>
                    </td>
                    <td className="py-2 px-4 text-[#BDFF00] font-bold">{q.options?.[q.answer]}</td>
                    <td className="py-2 px-4 text-xs max-w-xs whitespace-pre-line">{q.explanation || <span className="opacity-50">-</span>}</td>
                    <td className="py-2 px-4">
                      {q.active !== false ? (
                        <span className="flex items-center gap-1 text-green-400 font-bold"><CheckCircle2 size={16} /> Active</span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-400 font-bold"><XCircle size={16} /> Inactive</span>
                      )}
                    </td>
                    <td className="py-2 px-4 flex gap-2">
                      <button className="p-2 rounded hover:bg-[#FFE400]/20 transition" title="Éditer" onClick={() => handleEdit(q)}><Pencil size={18} /></button>
                      <button className="p-2 rounded hover:bg-red-500/30 transition" title="Supprimer" onClick={() => { setShowConfirm(true); setToDelete(q); }}><Trash2 size={18} /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Quiz; 